/**
 * Reports — Lead Command Center demo view.
 *
 * Pipeline funnel, source/campaign breakdowns, and outcomes, computed live by
 * the Supabase-native demo Worker (/api/lcc/reports).
 */

import { useQuery } from '@tanstack/react-query';
import { lccApi } from '@/lib/lcc-api';
import { Card } from '@/components/ui/card';
import {
  formatLccLabel,
  LccErrorCard,
  LccLoadingCard,
  LccPageHeader,
} from './lcc-shared';

export function LeadReportsView() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['lcc', 'reports'],
    queryFn: () => lccApi.reports(),
  });

  return (
    <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-4">
      <LccPageHeader
        title="Reports"
        blurb="Live pipeline counts straight from Supabase — how leads arrive, where they are in the funnel, and how they resolve."
      />
      {isLoading && <LccLoadingCard />}
      {error != null && <LccErrorCard title="Couldn't load reports" error={error} />}
      {data && (
        <>
          <section className="grid gap-3 sm:grid-cols-2">
            <BreakdownCard
              title={`Pipeline funnel (${data.total} leads total)`}
              rows={data.funnel.map((step) => ({ label: step.label, count: step.count }))}
            />
            <BreakdownCard
              title="Outcomes"
              rows={data.outcomes.map((o) => ({ label: o.label, count: o.count }))}
            />
          </section>
          <section className="grid gap-3 sm:grid-cols-2">
            <BreakdownCard
              title="Leads by source"
              rows={sortedEntries(data.bySource).map(([source, count]) => ({
                label: formatLccLabel(source),
                count,
              }))}
            />
            <BreakdownCard
              title="Leads by campaign"
              rows={sortedEntries(data.byCampaign).map(([campaign, count]) => ({
                label: campaign,
                count,
              }))}
              emptyLabel="No campaign-tagged leads."
            />
          </section>
          <BreakdownCard
            title="All statuses"
            rows={sortedEntries(data.byStatus).map(([status, count]) => ({
              label: formatLccLabel(status),
              count,
            }))}
          />
        </>
      )}
    </div>
  );
}

function BreakdownCard({
  title,
  rows,
  emptyLabel = 'Nothing here yet.',
}: {
  title: string;
  rows: Array<{ label: string; count: number }>;
  emptyLabel?: string;
}) {
  const max = Math.max(1, ...rows.map((row) => row.count));
  return (
    <Card className="p-4">
      <h2 className="text-[14px] font-semibold leading-tight">{title}</h2>
      <ul className="mt-3 space-y-2">
        {rows.length === 0 && <li className="text-[12px] text-muted-foreground">{emptyLabel}</li>}
        {rows.map((row) => (
          <li key={row.label} className="flex items-center gap-2">
            <span className="w-40 flex-shrink-0 truncate text-[12px] capitalize">{row.label}</span>
            <span
              className="h-2 rounded-full bg-primary/25"
              style={{ width: `${Math.max(4, Math.round((row.count / max) * 100))}%` }}
              aria-hidden
            />
            <span className="ml-auto text-[12px] font-medium tabular-nums">{row.count}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function sortedEntries(record: Record<string, number>): Array<[string, number]> {
  return Object.entries(record).sort((a, b) => b[1] - a[1]);
}
